import { useCallback, useEffect, useRef } from 'react'

/**
 * Syncs drawer open/close state with the browser URL bar.
 * Uses History API directly (not react-router navigate) so other
 * location-dependent hooks (useDateRangeWithURL, etc.) are NOT
 * re-triggered — the URL change is purely cosmetic / for deep-linking.
 *
 * Pattern: ?drawer=<type>&id=<id>
 *
 * @param {string} drawerType - e.g. 'call', 'campaign', 'qa'
 * @param {Object} options
 * @param {boolean} options.drawerOpen - Current drawer open state
 * @param {Function} options.onOpenFromURL - Called with id string when URL says drawer should open
 * @param {Function} options.onClose - Called when URL says drawer should close
 */
export default function useDrawerURL (drawerType, { drawerOpen, onOpenFromURL, onClose }) {
  const drawerOpenRef = useRef(drawerOpen)
  const onOpenFromURLRef = useRef(onOpenFromURL)
  const onCloseRef = useRef(onClose)

  drawerOpenRef.current = drawerOpen
  onOpenFromURLRef.current = onOpenFromURL
  onCloseRef.current = onClose

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlType = params.get('drawer')
    const urlId = params.get('id')

    if (urlType === drawerType && urlId && !drawerOpenRef.current) {
      onOpenFromURLRef.current(urlId)
    }
  }, [drawerType])

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)
      const urlType = params.get('drawer')
      const urlId = params.get('id')

      if (urlType === drawerType && urlId) {
        if (!drawerOpenRef.current) {
          onOpenFromURLRef.current(urlId)
        }
      } else if (drawerOpenRef.current) {
        onCloseRef.current()
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [drawerType])

  const openDrawerWithURL = useCallback((id) => {
    const url = new URL(window.location.href)
    url.searchParams.set('drawer', drawerType)
    url.searchParams.set('id', id)
    window.history.pushState(null, '', url.toString())
  }, [drawerType])

  const closeDrawerWithURL = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete('drawer')
    url.searchParams.delete('id')
    window.history.pushState(null, '', url.toString())
  }, [])

  return { openDrawerWithURL, closeDrawerWithURL }
}
